import { useEffect, type MutableRefObject } from 'react';
import type { SparkViewer } from '../utils/SparkViewer';
import { moveCameraOnGroundPlane, orbitCameraAroundHouse, zoomCameraAboutHouse } from '../utils/measureUtils';

export default function RealEstateNavigation({ viewerRef, center, onNavigate }: {
  viewerRef: MutableRefObject<unknown>;
  center: [number, number, number];
  onNavigate: () => void;
}) {
  useEffect(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !canvas) return;
    const restore = viewer.enableAerialNavigation();
    const oldTouchAction = canvas.style.touchAction;
    canvas.style.touchAction = 'none';
    const keys = new Set<string>();
    const pointers = new Map<number, { x: number; y: number }>();
    let previousPinch: number | null = null;
    let previousGestureScale = 1;
    let gestureActive = false;
    const editable = (target: EventTarget | null) => target instanceof HTMLElement
      && !!target.closest('input, textarea, select, [contenteditable="true"]');
    const zoom = (amount: number) => {
      onNavigate();
      zoomCameraAboutHouse(viewer.camera, center, amount);
      viewer.syncManualNavigation();
    };
    const down = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onNavigate();
      canvas.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      previousPinch = null;
    };
    const move = (event: PointerEvent) => {
      const last = pointers.get(event.pointerId);
      if (!last) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (previousPinch != null && distance > 0) zoom(Math.log(previousPinch / distance));
        previousPinch = distance;
      } else if (pointers.size === 1) {
        orbitCameraAroundHouse(viewer.camera, center,
          (event.clientX - last.x) * 0.005, (event.clientY - last.y) * 0.005);
        viewer.syncManualNavigation();
      }
    };
    const up = (event: PointerEvent) => {
      pointers.delete(event.pointerId);
      previousPinch = null;
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    };
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (gestureActive) return;
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? canvas.clientHeight : 1);
      // Requested pinch direction: fingers together zooms in; normal wheel-up zooms in.
      zoom(Math.max(-0.25, Math.min(0.25, delta * (event.ctrlKey ? 0.004 : -0.0015))));
    };
    const gestureStart = (event: Event) => {
      event.preventDefault();
      gestureActive = true;
      previousGestureScale = 1;
      onNavigate();
    };
    const gestureChange = (event: Event) => {
      event.preventDefault();
      const scale = (event as Event & { scale: number }).scale;
      if (scale > 0) zoom(Math.log(previousGestureScale / scale));
      previousGestureScale = scale;
    };
    const gestureEnd = () => { gestureActive = false; };
    const preventForceClick = (event: Event) => { event.preventDefault(); };
    const keyDown = (event: KeyboardEvent) => {
      if (editable(event.target) || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.code)) return;
      event.preventDefault();
      onNavigate();
      keys.add(event.code);
    };
    const keyUp = (event: KeyboardEvent) => { keys.delete(event.code); };
    const clear = () => { keys.clear(); pointers.clear(); previousPinch = null; };
    const visibility = () => { if (document.hidden) clear(); };
    let frame = 0;
    let lastTime: number | null = null;
    const tick = (now: number) => {
      const delta = lastTime == null ? 0 : Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      if (keys.size) {
        const right = Number(keys.has('ArrowRight')) - Number(keys.has('ArrowLeft'));
        const forward = Number(keys.has('ArrowUp')) - Number(keys.has('ArrowDown'));
        const step = delta * 10 / (right && forward ? Math.SQRT2 : 1);
        moveCameraOnGroundPlane(viewer.camera, right * step, forward * step);
        viewer.syncManualNavigation();
      }
      frame = requestAnimationFrame(tick);
    };
    canvas.addEventListener('pointerdown', down, true);
    canvas.addEventListener('pointermove', move, true);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('lostpointercapture', up);
    canvas.addEventListener('wheel', wheel, { capture: true, passive: false });
    canvas.addEventListener('gesturestart', gestureStart, { passive: false });
    canvas.addEventListener('gesturechange', gestureChange, { passive: false });
    canvas.addEventListener('gestureend', gestureEnd);
    canvas.addEventListener('webkitmouseforcewillbegin', preventForceClick, { passive: false });
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', visibility);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      canvas.removeEventListener('pointerdown', down, true);
      canvas.removeEventListener('pointermove', move, true);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      canvas.removeEventListener('lostpointercapture', up);
      canvas.removeEventListener('wheel', wheel, true);
      canvas.removeEventListener('gesturestart', gestureStart);
      canvas.removeEventListener('gesturechange', gestureChange);
      canvas.removeEventListener('gestureend', gestureEnd);
      canvas.removeEventListener('webkitmouseforcewillbegin', preventForceClick);
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      window.removeEventListener('blur', clear);
      document.removeEventListener('visibilitychange', visibility);
      canvas.style.touchAction = oldTouchAction;
      restore();
    };
  }, [viewerRef, center, onNavigate]);
  return null;
}
